import { useEffect, useRef } from "react";

/**
 * Hook to add scroll-triggered reveal animations.
 * Attaches an IntersectionObserver to the ref element.
 * When visible, adds 'visible' class to all children with reveal classes.
 * Uses MutationObserver to handle dynamically loaded content.
 */
export const useScrollReveal = (options = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let containerVisible = false;

    const revealAllChildren = () => {
      el.querySelectorAll(
        ".reveal:not(.visible), .reveal-left:not(.visible), .reveal-right:not(.visible), .reveal-scale:not(.visible)"
      ).forEach((child) => child.classList.add("visible"));
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            containerVisible = true;
            revealAllChildren();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: options.threshold || 0.1, rootMargin: options.rootMargin || "0px 0px -40px 0px" }
    );

    // Always observe the container element
    observer.observe(el);

    // Also observe any existing child reveal elements
    el.querySelectorAll(".reveal, .reveal-left, .reveal-right, .reveal-scale").forEach(
      (child) => observer.observe(child)
    );

    // MutationObserver for dynamically loaded children (e.g., API data)
    const mutationObserver = new MutationObserver(() => {
      if (containerVisible) {
        // Container already visible, reveal new children immediately
        revealAllChildren();
      } else {
        // Container not yet visible, observe new children
        el.querySelectorAll(
          ".reveal:not(.visible), .reveal-left:not(.visible), .reveal-right:not(.visible), .reveal-scale:not(.visible)"
        ).forEach((child) => observer.observe(child));
      }
    });

    mutationObserver.observe(el, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [options.threshold, options.rootMargin]);

  return ref;
};
